/**
 * One-Euro filter (Casiez et al., CHI 2012) — the de-facto standard for
 * smoothing noisy human-motion signals with minimal lag: heavy smoothing when
 * the signal is slow (kills jitter), light smoothing when it moves fast
 * (keeps saccades responsive). Used to stabilise the live gaze dot.
 */

class LowPass {
  private initialized = false
  private stored = 0

  filter(value: number, alpha: number): number {
    if (!this.initialized) {
      this.initialized = true
      this.stored = value
      return value
    }
    this.stored = alpha * value + (1 - alpha) * this.stored
    return this.stored
  }

  last(): number {
    return this.stored
  }

  reset(): void {
    this.initialized = false
  }
}

export class OneEuroFilter {
  private readonly minCutoff: number
  private readonly beta: number
  private readonly dCutoff: number
  private readonly xFilter = new LowPass()
  private readonly dxFilter = new LowPass()
  private lastTime: number | null = null

  /**
   * @param minCutoff base cutoff frequency (Hz) — lower = smoother at rest
   * @param beta speed coefficient — higher = less lag during fast movement
   */
  constructor(minCutoff = 1.0, beta = 0.02, dCutoff = 1.0) {
    this.minCutoff = minCutoff
    this.beta = beta
    this.dCutoff = dCutoff
  }

  private static alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff)
    return 1 / (1 + tau / dt)
  }

  /** @param t timestamp in seconds */
  filter(value: number, t: number): number {
    if (this.lastTime === null) {
      this.lastTime = t
      this.dxFilter.filter(0, 1)
      return this.xFilter.filter(value, 1)
    }
    const dt = Math.max(t - this.lastTime, 1e-6)
    this.lastTime = t
    const dx = (value - this.xFilter.last()) / dt
    const edx = this.dxFilter.filter(dx, OneEuroFilter.alpha(this.dCutoff, dt))
    const cutoff = this.minCutoff + this.beta * Math.abs(edx)
    return this.xFilter.filter(value, OneEuroFilter.alpha(cutoff, dt))
  }

  reset(): void {
    this.lastTime = null
    this.xFilter.reset()
    this.dxFilter.reset()
  }
}

/** Convenience 2D wrapper for screen-point smoothing. */
export class OneEuroFilter2D {
  private readonly fx: OneEuroFilter
  private readonly fy: OneEuroFilter

  constructor(minCutoff = 1.0, beta = 0.02, dCutoff = 1.0) {
    this.fx = new OneEuroFilter(minCutoff, beta, dCutoff)
    this.fy = new OneEuroFilter(minCutoff, beta, dCutoff)
  }

  filter(x: number, y: number, tSeconds: number): { x: number; y: number } {
    return { x: this.fx.filter(x, tSeconds), y: this.fy.filter(y, tSeconds) }
  }

  reset(): void {
    this.fx.reset()
    this.fy.reset()
  }
}
